<template>
    <div class="bento-layout">
        <!-- Status & Regal Container -->
        <div class="bento-header">
            <div class="bento-status">
                <div class="status-label">Status</div>
                <div class="status-value">Scannen</div>
            </div>
            <div class="bento-slot">
                <div class="slot-label">Regal</div>
                <div class="slot-value">{{ formattedSlotName }}</div>
            </div>
        </div>

        <!-- Preview Container - Flexible -->
        <div class="bento-preview">
            <!-- Image Preview -->
            <div v-if="isImageUrl(articleData.printPreview)" class="preview-content">
                <img
                    :src="articleData.printPreview"
                    alt="Preview"
                    class="preview-img"
                    @error="handleImageError"
                />
            </div>
            <!-- Text Preview -->
            <div v-else class="preview-content preview-text">
                <p>{{ articleData.printPreview }}</p>
            </div>
        </div>

        <!-- Message Container -->
        <div class="bento-message">
            <v-icon color="white" size="20" class="pulse-icon">mdi-qrcode-scan</v-icon>
            <span>{{ slotHeaderText }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ScanStartState',
    props: {
        articleData: {
            type: Object,
            required: true,
            default: () => ({
                orderId: '',
                sku: '',
                shelfLocation: '',
                printPreview: '',
                originalSlot: '',
                requiresExtraSlot: false
            })
        }
    },
    data() {
        return {
            imageLoadError: false
        };
    },
    computed: {
        isSmallScreen() {
            return window.innerWidth <= 480;
        },
        slotHeaderText() {
            if (this.articleData.requiresExtraSlot) {
                return 'Sperrgut-Regal';
            }
            return 'Scannen Sie Regal';
        },
        formattedSlotName() {
            const slotName = this.articleData.shelfLocation || '';

            // If it's an extra slot starting with SPERRGUT_, format it nicely
            if (slotName.startsWith('SPERRGUT_')) {
                // Remove SPERRGUT_ prefix and convert to title case
                const name = slotName.replace('SPERRGUT_', '');
                // Convert underscores to spaces and capitalize first letter
                return name.replace(/_/g, ' ').toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }

            return slotName;
        }
    },
    methods: {
        isImageUrl(str) {
            if (!str) return false;
            const isUrl = str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:image');
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
            const hasImageExtension = imageExtensions.some(ext => str.toLowerCase().includes(ext));
            return isUrl || hasImageExtension;
        },
        handleImageError(event) {
            console.error('Failed to load image:', this.articleData.printPreview);
            this.imageLoadError = true;
            event.target.style.display = 'none';
        }
    }
};
</script>

<style scoped>
/* Bento Layout - Responsive Grid */
.bento-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-height: 0;
    overflow: hidden;
    gap: 8px;
    padding: 8px;
    box-sizing: border-box;
    background: #ffffff !important;
}

/* Header: Status & Regal */
.bento-header {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    height: auto;
}

.bento-status,
.bento-slot {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    border-radius: 8px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    text-align: center;
}

.status-label,
.slot-label {
    font-size: clamp(8px, 2vw, 10px);
    opacity: 0.9;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-value {
    font-size: clamp(12px, 3.5vw, 16px);
    font-weight: bold;
    margin-top: 2px;
}

.slot-value {
    font-size: clamp(14px, 4vw, 20px);
    font-weight: bold;
    margin-top: 2px;
    word-break: break-word;
    line-height: 1;
}

/* Preview Container - Flexible */
.bento-preview {
    flex: 1;
    min-height: 0;
    background: white;
    border-radius: 8px;
    border: 2px solid #e0e0e0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
}

.preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.preview-text {
    flex-direction: column;
}

.preview-text p {
    font-size: clamp(12px, 3vw, 16px);
    font-weight: 600;
    color: #333;
    margin: 0;
    text-align: center;
    word-break: break-word;
}

/* Message Container */
.bento-message {
    flex-shrink: 0;
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    border-radius: 8px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: white;
    font-size: clamp(11px, 2.8vw, 14px);
    font-weight: 500;
}

.pulse-icon {
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.7;
    }
}

/* Responsive adjustments */
@media (max-width: 480px) {
    .bento-layout {
        gap: 6px;
        padding: 6px;
    }

    .bento-header {
        gap: 6px;
    }

    .bento-status,
    .bento-slot {
        padding: 5px;
    }

    .preview-content {
        padding: 4px;
    }

    .bento-message {
        padding: 6px;
        gap: 6px;
    }
}
</style>
